import { AnyError } from '@microsoft/portal-app/lib/IAnyError';
import { QueryStatus, useQuery } from 'react-query';
import { getBatchUpdateUrl } from '../../redux/modules/graphApis';
import {
  IGetRegionalAndLanguageSettingsBatchResponse,
  ILocaleInfo,
  IRegionalAndLanguageSettings,
  IRegionalFormatEntry,
  IRegionalFormatOverrides,
  IRegionalFormattingPreference,
  ITranslationPreferences,
} from '../models';
import { QueryKey } from '../models/QueryKey';
import { useAuth } from '@iamexperiences/feature-auth';
import { useReportAPITelemetry } from './useReportAPITelemetry';
import { HttpRequestError } from '../../shared/HttpRequestError';
import { settingsManager } from '../../shared/settings';
import { isHttpSuccess } from '../utilities/isHttpSuccess';
import { getMSGraphScopeAndEndPoint } from '../../shared/utils';
import { MSGraphScopes } from '../../shared/constants';

export interface IRegionalAndLanguageSettingsQueryResult {
  readonly isLoading: boolean;
  readonly status: QueryStatus;
  readonly authoringLanguages: string[];
  readonly translationLanguage: string;
  readonly speechLanguage: string;
  readonly defaultDisplayLanguage: string;
  readonly defaultRegionalFormat: IRegionalFormatEntry;
  readonly regionalFormatOverrides: IRegionalFormatOverrides;
  readonly isMailboxAvailable: boolean | undefined;
  readonly translationPreferences: ITranslationPreferences;
}

const defaultRegionalAndLanguageSettingsData: IRegionalAndLanguageSettingsQueryResult = {
  isLoading: false,
  status: 'idle',
  authoringLanguages: [],
  translationLanguage: '',
  speechLanguage: '',
  defaultDisplayLanguage: '',
  defaultRegionalFormat: { locale: '', displayName: '', localizedName: '' },
  regionalFormatOverrides: {
    calendar: null,
    firstDayOfWeek: null,
    longDateFormat: null,
    longTimeFormat: null,
    shortTimeFormat: null,
    shortDateFormat: null,
    timeZone: null,
  },
  isMailboxAvailable: undefined,
  translationPreferences: {
    translationBehavior: '',
  },
};

const convert = (localeInfo: ILocaleInfo | null): string => {
  return localeInfo ? localeInfo.locale : '';
};

export const useGetRegionalAndLanguageSettingsQuery = (): IRegionalAndLanguageSettingsQueryResult => {
  const isSupported = !settingsManager.get('isPreferredLanguagesDisabled');

  const { auth } = useAuth();
  const { startBatchTelemetryReport, stopBatchTelemetryReport, stopTelemetryReport } = useReportAPITelemetry(
    'getRegionalAndLanguageSettings',
  );

  const { data, isLoading, status } = useQuery<IRegionalAndLanguageSettingsQueryResult, AnyError>(
    QueryKey.REGIONAL_AND_LANGUAGE_SETTINGS,
    async () => {
      const requestBody = JSON.stringify({
        requests: [
          {
            id: '1',
            method: 'GET',
            url: '/me/settings/regionalAndLanguageSettings',
            headers: {
              Accept: 'application/json',
            },
          },
          {
            id: '2',
            dependsOn: ['1'],
            method: 'GET',
            url: '/me/mailboxSettings',
            headers: {
              Accept: 'application/json',
            },
          },
        ],
      });

      const url = getBatchUpdateUrl();
      const token = await auth.acquireToken({
        scopes: [
          getMSGraphScopeAndEndPoint(MSGraphScopes.UserReadAll).scope,
          getMSGraphScopeAndEndPoint(MSGraphScopes.MailboxSettingsReadWrite).scope,
        ],
      });

      startBatchTelemetryReport(JSON.parse(requestBody).requests);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
        body: requestBody,
      });

      if (!response.ok) {
        const error = new HttpRequestError(response.status, response.statusText);
        stopTelemetryReport('error', error);
        throw error;
      }

      const data = (await response.json()) as IGetRegionalAndLanguageSettingsBatchResponse<
        IRegionalAndLanguageSettings,
        IRegionalFormattingPreference
      >;

      const responses = data.responses;
      if (responses) {
        responses.forEach((item) => {
          stopBatchTelemetryReport(item.id, item);
        });
      }

      if (!data || !isHttpSuccess(data.responses[0].status)) {
        return { ...defaultRegionalAndLanguageSettingsData };
      }

      const {
        authoringLanguages,
        defaultTranslationLanguage,
        defaultSpeechInputLanguage,
        defaultDisplayLanguage,
        translationPreferences,
      } = data.responses[0].body;

      const { defaultRegionalFormat, regionalFormatOverrides, isMailboxReqSuccess } = mergeRegionalSettings(data);

      const payload: IRegionalAndLanguageSettingsQueryResult = {
        isLoading: false,
        status: status,
        defaultDisplayLanguage: convert(defaultDisplayLanguage),
        authoringLanguages: authoringLanguages.map((lang) => convert(lang)),
        translationLanguage: convert(defaultTranslationLanguage),
        speechLanguage: convert(defaultSpeechInputLanguage),
        defaultRegionalFormat,
        regionalFormatOverrides,
        isMailboxAvailable: isMailboxReqSuccess,
        translationPreferences,
      };

      return payload;
    },
    { enabled: isSupported, notifyOnChangeProps: ['data', 'error'] },
  );

  if (!data) {
    return { ...defaultRegionalAndLanguageSettingsData, isLoading: isLoading, status: status };
  }

  return { ...data, isLoading: isLoading, status: status };
};

const mergeRegionalSettings = (
  data: IGetRegionalAndLanguageSettingsBatchResponse<IRegionalAndLanguageSettings, IRegionalFormattingPreference>,
) => {
  let { defaultRegionalFormat, regionalFormatOverrides } = data.responses[0].body;
  const isMailboxReqSuccess = isHttpSuccess(data.responses[1].status);
  regionalFormatOverrides = regionalFormatOverrides ?? defaultRegionalAndLanguageSettingsData.regionalFormatOverrides;

  if (isMailboxReqSuccess) {
    const { timeFormat, dateFormat, timeZone, language } = data.responses[1].body;

    // FAST API validates regional format override parameters. null is valid input but empty string '' is not.
    const fallbackRegionalFormat = language && language.locale ? language : null;
    const fallbackDateFormat = dateFormat ? dateFormat : null;
    const fallbackTimeFormat = timeFormat ? timeFormat : null;
    const fallbackTimezone = timeZone ? timeZone : null;

    defaultRegionalFormat = defaultRegionalFormat ?? fallbackRegionalFormat;
    regionalFormatOverrides = {
      ...regionalFormatOverrides,
      shortDateFormat: regionalFormatOverrides?.shortDateFormat ?? fallbackDateFormat,
      shortTimeFormat: regionalFormatOverrides?.shortTimeFormat ?? fallbackTimeFormat,
      timeZone: regionalFormatOverrides?.timeZone ?? fallbackTimezone,
    };
  }
  defaultRegionalFormat = defaultRegionalFormat ?? defaultRegionalAndLanguageSettingsData.defaultRegionalFormat;

  return { defaultRegionalFormat, regionalFormatOverrides, isMailboxReqSuccess };
};
